import React, { useState, useEffect, useRef } from "react";
import { formatDateForDisplay, formatDateForInput } from "../utils/formatters";
import { formatNumericInput } from "../utils/validationHelpers";
import {
  getHtmlDateMinMax,
  normalizeToYmd,
  validateScheduleFutureYmd,
} from "../utils/dateFieldConstraints";
import {
  DEPOSIT_METHOD_OPTIONS,
  FREQUENCY_OPTIONS,
} from "../constants/accountConstants";

function AddContributionModal({ isOpen, onClose, account, onSuccess }) {
  const [formData, setFormData] = useState({
    amount: "",
    frequency: "",
    start_date: "",
    method: "ACH"
  });
  const [error, setError] = useState("");
  const dateInputRef = useRef(null);

  const handleChange = (field, value) => {
    let sanitizedValue = value;
    
    if (field === "amount") {
      sanitizedValue = formatNumericInput(value, true);
    }

    setFormData(prev => ({ ...prev, [field]: sanitizedValue }));
    setError("");
  };

  const getInitialFormState = () => ({
    amount: "",
    frequency: "",
    start_date: "",
    method: "ACH"
  });

  const validateForm = () => {
    if (formData.amount === "" || formData.amount === null || formData.amount === undefined) {
      setError("Amount is required");
      return false;
    }
    const amountNum = parseFloat(formData.amount);
    if (isNaN(amountNum) || amountNum <= 0) {
      setError("Amount must be a positive number");
      return false;
    }
    if (!formData.frequency) {
      setError("Frequency is required");
      return false;
    }
    if (!formData.method) {
      setError("Method is required");
      return false;
    }
    if (formData.start_date?.trim()) {
      const msg = validateScheduleFutureYmd(
        normalizeToYmd(formData.start_date),
        "Start date",
      );
      if (msg) {
        setError(msg);
        return false;
      }
    }
    return true;
  };

  const handleSubmit = () => {
    if (!validateForm() || !account?.id) return;

    const startDateStr = formData.start_date?.trim();
    const depositPayload = {
      amount: parseFloat(formData.amount),
      frequency: formData.frequency,
      start_date: startDateStr ? formatDateForInput(startDateStr) : null,
      method: formData.method
    };

    setFormData(getInitialFormState());
    onSuccess(depositPayload);
  };

  const handleClose = () => {
    setFormData(getInitialFormState());
    setError("");
    onClose();
  };

  useEffect(() => {
    if (isOpen) {
      setFormData(getInitialFormState());
      setError("");
    }
  }, [isOpen]);

  if (!isOpen) return null;

  const scheduleBounds = getHtmlDateMinMax("schedule_future");

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 rounded-t-xl">
          <div className="flex justify-between items-center">
            <h2 className="text-2xl font-bold text-gray-800">Add Contribution</h2>
            <button
              onClick={handleClose}
              className="text-gray-400 hover:text-gray-600 transition-colors"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>

        <div className="p-6 space-y-6">
          {error && (
            <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg">
              {error}
            </div>
          )}

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Amount <span className="text-red-500">*</span>
              </label>
              <input
                type="number"
                min="0"
                step="any"
                value={formData.amount}
                onChange={(e) => handleChange("amount", e.target.value)}
                onWheel={(e) => e.target.blur()}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                placeholder="0.00"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Frequency <span className="text-red-500">*</span>
              </label>
              <select
                value={formData.frequency}
                onChange={(e) => handleChange("frequency", e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
              >
                <option value="">Select frequency</option>
                {FREQUENCY_OPTIONS.map(opt => (
                  <option key={opt} value={opt}>{opt}</option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Start Date
              </label>
              <div
                className="relative cursor-pointer"
                role="button"
                tabIndex={0}
                onClick={() => { if (dateInputRef.current?.showPicker) dateInputRef.current.showPicker(); else dateInputRef.current?.focus(); }}
                onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); dateInputRef.current?.showPicker?.(); } }}
              >
                <div className="relative">
                  <input
                    type="text"
                    readOnly
                    value={formData.start_date ? formatDateForDisplay(formatDateForInput(formData.start_date)) : ""}
                    placeholder="mm-dd-yyyy"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 bg-white cursor-pointer pointer-events-none pr-10"
                    aria-readonly
                  />
                  <div className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none">
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                    </svg>
                  </div>
                </div>
                <input
                  ref={dateInputRef}
                  type="date"
                  min={scheduleBounds.min}
                  max={scheduleBounds.max}
                  value={formData.start_date ? formatDateForInput(formData.start_date) : ""}
                  onChange={(e) => handleChange("start_date", e.target.value ? formatDateForInput(e.target.value) : "")}
                  onKeyDown={(e) => e.preventDefault()}
                  onPaste={(e) => e.preventDefault()}
                  onWheel={(e) => e.target.blur()}
                  className="absolute inset-0 w-full h-full opacity-0 cursor-pointer pointer-events-none"
                  style={{ cursor: "pointer" }}
                  tabIndex={-1}
                  aria-hidden
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Method <span className="text-red-500">*</span>
              </label>
              <select
                value={formData.method}
                onChange={(e) => handleChange("method", e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
              >
                <option value="">Select method</option>
                {DEPOSIT_METHOD_OPTIONS.map(opt => (
                  <option key={opt} value={opt}>{opt}</option>
                ))}
              </select>
            </div>
          </div>
        </div>

        <div className="sticky bottom-0 bg-gray-50 px-6 py-4 border-t border-gray-200 rounded-b-xl flex justify-end gap-3">
          <button
            onClick={handleClose}
            className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-100 transition-colors disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            className="px-4 py-2 bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg font-semibold transition-colors disabled:opacity-50 flex items-center gap-2"
          >
            Add Contribution
          </button>
        </div>
      </div>
    </div>
  );
}

export default AddContributionModal;
